import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ChevronDownIcon, PlayIcon, BookOpenIcon, CheckBadgeIcon,
    ArrowRightIcon, DocumentArrowDownIcon, SparklesIcon,
    CheckCircleIcon, AcademicCapIcon, LockClosedIcon
} from '@heroicons/react/24/solid';
import { useNavigate, useParams } from 'react-router-dom'; 
import { db, auth } from '../../firebase'; 
import { doc, getDoc, collection, getDocs, query, orderBy, updateDoc, increment } from 'firebase/firestore';

// ⬇️ [SECTION: FULLSCREEN_&_ROTATION_STYLES] - نظام التدوير والملء الشامل (المطور 2026 🛡️)
const fullscreenStyles = `
  video::-webkit-media-controls-panel { width: 100%; }
  
  /* إجبار الفيديو على أخذ كامل مساحة الشاشة في وضع التكبير */
  video:-webkit-full-screen { width: 100vw !important; height: 100vh !important; object-fit: contain !important; background: black !important; }
  video:fullscreen { width: 100vw !important; height: 100vh !important; object-fit: contain !important; background: black !important; }

  /* التفاعل مع تدوير الشاشة (للموبايل والتابلت) */
  @media screen and (orientation: landscape) {
    video:fullscreen {
      object-fit: contain !important;
      width: 100vw !important;
      height: 100vh !important;
    }
  }

  /* منع ظهور هوامش المتصفح وتحسين اللمس */
  video { width: 100%; height: auto; touch-action: manipulation; }
`;

const CourseView = () => {
    const { type } = useParams(); 
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const lastTimeRef = useRef(0); // [REF: SEEK_PROTECTION] - لحفظ آخر ثانية شاهدها الطالب ومنع التقديم
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allLessons, setAllLessons] = useState([]); 
    const [activeVideo, setActiveVideo] = useState(null);
    const [openSection, setOpenSection] = useState(""); 
    const [openChapter, setOpenChapter] = useState(""); 
    const [isVideoEnded, setIsVideoEnded] = useState(false);

    const startTimeRef = useRef(Date.now());

    // ⬇️ [LOGIC: FULLSCREEN_ROTATION_LOCK] - دالة قفل التدوير عند التكبير لراحة الطالب
    const handleFullScreenChange = () => {
        if (document.fullscreenElement && window.screen.orientation && window.screen.orientation.lock) {
            // يحاول قفل الشاشة على وضع العرض عند التكبير لراحة الطالب (تعمل على الأندرويد والتابات)
            window.screen.orientation.lock('landscape').catch(() => {
                console.log("Orientation lock not supported on this device.");
            });
        }
    };

    // ⬇️ [EFFECT: ROTATION_DETECTOR] - ربط مستشعرات التغيير في وضع ملء الشاشة
    useEffect(() => {
        document.addEventListener('fullscreenchange', handleFullScreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
        };
    }, []);

    const saveStudyTime = async () => {
        if (!auth.currentUser) return;
        const endTime = Date.now();
        const durationMs = endTime - startTimeRef.current;
        const minutesSpent = Math.floor(durationMs / 60000);
        if (minutesSpent > 0) {
            try {
                const userRef = doc(db, "users", auth.currentUser.uid);
                await updateDoc(userRef, { totalStudyMinutes: increment(minutesSpent) });
            } catch (err) { console.error("Study time save failed:", err); }
        }
    };

    // [REF: CLEAN_FUNCTION] - دالة تنظيف النصوص الموحدة لمنع مشاكل الهمزات
    const clean = (str) => (str || "").toString().trim().replace(/[أإآا]/g, "ا").toLowerCase();

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (auth.currentUser) {
                    const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                    const uData = userDoc.exists() ? userDoc.data() : null;

                    const lessonsQuery = query(collection(db, "lessons"), orderBy("createdAt", "asc"));
                    const querySnapshot = await getDocs(lessonsQuery);
                    const lessonsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    
                    // [REF: IRON_FILTER_LOGIC] - نظام الفلترة الحديدي المطور
                    const filtered = lessonsList.filter(lesson => {
                        if (lesson.status === "disabled") return false;

                        const lessonCourse = clean(lesson.courseType);
                        const pageType = clean(type); 

                        const isInsideC1 = pageType.includes("1") || pageType.includes("اول");
                        const isInsideC2 = pageType.includes("2") || pageType.includes("ثاني");

                        const subType = clean(uData?.subscriptionType || uData?.activeCourse || "");
                        const hasFull = subType.includes("كامل") || subType.includes("كورسين");

                        if (lessonCourse.includes("عام")) return true;

                        if (isInsideC1) {
                            const hasC1Auth = subType.includes("اول") || subType.includes("1") || uData?.isCourse1Active || hasFull;
                            return (lessonCourse.includes("اول") || lessonCourse.includes("1")) && hasC1Auth;
                        }

                        if (isInsideC2) {
                            const hasC2Auth = subType.includes("ثاني") || subType.includes("2") || uData?.isCourse2Active || hasFull;
                            return (lessonCourse.includes("ثاني") || lessonCourse.includes("2")) && hasC2Auth;
                        }

                        return false;
                    });

                    setAllLessons(filtered);
                    setUserData(uData);

                    if (filtered.length > 0) {
                        // ✅ [THE_AUTO_OPEN_FIX]: الفتح التلقائي للفصل والقسم بناءً على المحاضرة المختارة
                        const firstLesson = filtered[0];
                        setActiveVideo(firstLesson);
                        setOpenSection(firstLesson.courseType); 
                        setOpenChapter(firstLesson.chapter);    
                    } else {
                        setActiveVideo(null);
                    }
                }
            } catch (error) { 
                console.error("Fetch Error:", error); 
            } finally { 
                setLoading(false); 
            }
        };
        fetchData();
        return () => { saveStudyTime(); };
    }, [type]); 

    // ✅ [LOGIC: PREVENT_SEEKING] - دالة منع تقديم الفيديو لضمان المشاهدة الكاملة
    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        const currentTime = videoRef.current.currentTime;
        if (currentTime > lastTimeRef.current + 2) {
            videoRef.current.currentTime = lastTimeRef.current;
        } else {
            lastTimeRef.current = currentTime;
        }
    };

    const organizedStructure = () => {
        const structure = {};
        allLessons.forEach(lesson => {
            const courseLabel = lesson.courseType || "محاضرات عامة";
            const chapterLabel = lesson.chapter || "الفصول الدراسية";
            if (!structure[courseLabel]) structure[courseLabel] = {};
            if (!structure[courseLabel][chapterLabel]) structure[courseLabel][chapterLabel] = [];
            structure[courseLabel][chapterLabel].push(lesson);
        });
        return structure;
    };

    const handleVideoEnd = async () => {
        setIsVideoEnded(true);
        if (auth.currentUser && activeVideo) {
            try {
                const userRef = doc(db, "users", auth.currentUser.uid);
                await updateDoc(userRef, { overallProgress: increment(1) });
                await saveStudyTime();
                startTimeRef.current = Date.now(); 
            } catch (err) { console.error("Update Progress Error:", err); }
        }
    };

    // [SECTION: LESSON_RENDER_LOGIC] - منطق عرض المحاضرة مع الحماية من القفل
    const renderLessonContent = () => {
        // 🔒 [GUARD: LOCK_CHECK] - التحقق إذا كانت المحاضرة مقفولة من لوحة المدرس
        if (activeVideo?.isLocked === true || activeVideo?.status !== 'active') {
            return (
                <div className="flex items-center justify-center min-h-[500px]">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }} 
                        animate={{ opacity: 1, scale: 1 }}
                        className="ios-glass p-12 rounded-[50px] text-center space-y-8 border border-white/40 shadow-2xl bg-white/30 backdrop-blur-3xl max-w-lg w-full"
                    >
                        <div className="w-24 h-24 bg-red-100 rounded-[35px] flex items-center justify-center mx-auto mb-2 shadow-inner">
                            <LockClosedIcon className="w-12 h-12 text-red-500" />
                        </div>
                        
                        <div className="space-y-4">
                            <h3 className="text-4xl font-black text-gray-900 leading-tight">المحاضرة متوقفة</h3>
                            <p className="text-gray-600 font-bold px-4 leading-relaxed text-lg">
                                نعتذر منك يا بطل، تم إيقاف الوصول لهذه المحاضرة مؤقتاً     {activeVideo?.instructor || "نور عماد"}.
                            </p>
                        </div>

                        <div className="flex flex-col gap-4 justify-center pt-2">
                            <button onClick={() => navigate(-1)} className="px-10 py-5 bg-gray-900 text-white rounded-[25px] font-black shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2">
                                <ArrowRightIcon className="w-5 h-5 rotate-180" /> العودة للدروس
                            </button>
                            <button onClick={() => window.location.reload()} className="px-10 py-5 bg-[#007AFF] text-white rounded-[25px] font-black shadow-lg shadow-blue-500/20 active:scale-95 transition-all">
                                تحديث حالة المحاضرة
                            </button>
                        </div>
                        
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Security Check System • 2026</p>
                    </motion.div>
                </div>
            );
        }

        // [SUB-SECTION: VIDEO_PLAYER] - مشغل الفيديو الأصلي المحمي مع دعم التدوير والملء 🛡️
        return (
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
                <style>{fullscreenStyles}</style>
                <div className="relative shadow-[0_32px_64px_-15px_rgba(0,0,0,0.3)] rounded-[45px] overflow-hidden bg-black border-[6px] border-white/20 transition-transform duration-700">
                    <video 
                        ref={videoRef} 
                        key={activeVideo.url} 
                        onEnded={handleVideoEnd} 
                        onTimeUpdate={handleTimeUpdate} // [ENABLE: SEEK_LOCK] تفعيل قفل التقديم
                        controls 
                        controlsList="nodownload" 
                        className="w-full aspect-video"
                        playsInline // [FIX: IOS_TABLET] لضمان استقرار العرض على آبل
                    >
                        <source src={activeVideo.url} type="video/mp4" />
                    </video>
                </div>

                <div className="mt-8 p-8 ios-glass rounded-[40px] border border-white/40 shadow-xl bg-white/50 backdrop-blur-3xl text-right">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                        <div className="space-y-4 w-full">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#007AFF]/10 rounded-full border border-[#007AFF]/20 text-[#007AFF]">
                                <AcademicCapIcon className="w-4 h-4" />
                                <span className="text-[11px] font-black">{activeVideo.chapter}</span>
                            </div>
                            <h2 className="text-3xl font-black text-gray-900 leading-tight">{activeVideo.title}</h2>
                            
                            {/* [FIXED: DESCRIPTION_DISPLAY] - تفعيل وصف المحاضرة بستايل ملكي */}
                            {activeVideo.description && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }} 
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-5 bg-white/40 dark:bg-black/10 rounded-3xl border border-white/20 shadow-inner"
                                >
                                    <h4 className="text-xs font-black text-[#007AFF] mb-2 uppercase tracking-widest flex items-center gap-2">
                                        <SparklesIcon className="w-4 h-4" /> وصف المحاضرة
                                    </h4>
                                    <p className="text-sm font-bold text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap font-cairo">
                                        {activeVideo.description}
                                    </p>
                                </motion.div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-3 w-full md:w-auto shrink-0">
                            {activeVideo.pdfUrl && (
                                <a href={activeVideo.pdfUrl} target="_blank" rel="noreferrer" className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-4 ios-glass rounded-2xl font-black text-sm text-gray-800 border border-white/50 hover:bg-white transition-all shadow-lg text-center font-cairo">
                                    <DocumentArrowDownIcon className="w-5 h-5 text-red-500" /> تحميل الملزمة
                                </a>
                            )}
                            <div className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-black text-sm transition-all shadow-lg font-cairo ${isVideoEnded ? 'bg-green-500 text-white' : 'bg-white/40 text-gray-400'}`}>
                                <CheckCircleIcon className="w-5 h-5" /> {isVideoEnded ? "تم الإكمال" : "قيد المشاهدة"}
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-transparent">
            <div className="relative w-20 h-20">
                <div className="absolute inset-0 border-4 border-[#007AFF]/20 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin"></div>
            </div>
        </div>
    );

    // [REF: SECURITY_CHECK] - حماية نهائية إذا كانت القائمة فارغة
    const isAllowed = allLessons.length > 0;

    if (!isAllowed) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-transparent text-right" dir="rtl">
                <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="ios-glass p-12 rounded-[50px] shadow-2xl text-center max-w-md border border-white/40 bg-white/40 backdrop-blur-3xl">
                    <div className="w-24 h-24 bg-red-100 rounded-[35px] flex items-center justify-center mx-auto mb-6">
                        <LockClosedIcon className="w-12 h-12 text-red-500" />
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 mb-4">المحتوى مقفل</h2>
                    <p className="text-gray-500 font-bold mb-8 leading-relaxed">عذراً بطل، هذا الكورس غير متاح في اشتراكك الحالي.</p>
                    <button onClick={() => navigate('/student/activate')} className="w-full py-5 bg-[#007AFF] text-white rounded-[25px] font-black text-lg shadow-lg active:scale-95 transition-all flex items-center justify-center gap-3 font-cairo">
                        <SparklesIcon className="w-6 h-6" /> تفعيل كود جديد
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: '"Cairo", sans-serif' }} className="min-h-screen bg-transparent p-4 md:p-6 text-right transition-all" dir="rtl">
            <header className="mb-8 ios-glass p-5 rounded-[30px] border border-white/30 flex justify-between items-center shadow-lg bg-white/40 backdrop-blur-2xl">
                <div className="flex items-center gap-5">
                    <button onClick={() => navigate('/student/dashboard')} className="p-3 bg-white/60 rounded-2xl hover:bg-[#007AFF] hover:text-white transition-all shadow-sm">
                        <ArrowRightIcon className="w-6 h-6 rotate-180" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-black text-gray-900 tracking-tight">نور أكاديمي</h1>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            <p className="text-[12px] font-bold text-gray-500 uppercase tracking-tighter">بإشراف الأستاذة نور عماد</p>
                            <span className="text-gray-300">|</span>
                            <p className="text-[12px] font-black text-[#007AFF]">مرحباً بك: {userData?.name?.split(' ')[0] || "بطلنا"}</p>
                        </div>
                    </div>
                </div>
                <div className="hidden md:flex items-center gap-3">
                    <div className="px-5 py-2.5 bg-[#007AFF]/10 rounded-2xl border border-[#007AFF]/20 font-black text-xs text-[#007AFF]">
                        {userData?.subscriptionType || "طالب متميز"}
                    </div>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                <div className="lg:col-span-3 space-y-6">
                    {activeVideo ? renderLessonContent() : null}
                </div>

                <aside className="lg:col-span-1 space-y-6 text-right">
                    <div className="flex items-center justify-between px-4">
                        <h3 className="text-xl font-black text-gray-900">محاضرات الكورس</h3>
                        <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 flex items-center justify-center text-[10px] font-black text-[#007AFF]">iOS</div>
                    </div>

                    <div className="max-h-[75vh] overflow-y-auto custom-scroll px-1 space-y-4">
                        {Object.entries(organizedStructure()).map(([courseTitle, chapters], cIdx) => (
                            <div key={cIdx} className="ios-glass rounded-[35px] border border-white/30 overflow-hidden shadow-md bg-white/30 backdrop-blur-xl">
                                <button onClick={() => setOpenSection(openSection === courseTitle ? "" : courseTitle)} className="w-full p-5 flex justify-between items-center hover:bg-white/40 transition-all text-right">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all shadow-inner ${openSection === courseTitle ? 'bg-[#007AFF] text-white' : 'bg-white/60 text-gray-400'}`}>
                                            <BookOpenIcon className="w-6 h-6" />
                                        </div>
                                        <span className="font-black text-sm text-gray-800">{courseTitle}</span>
                                    </div>
                                    <ChevronDownIcon className={`w-5 h-5 transition-transform duration-500 ${openSection === courseTitle ? 'rotate-180 text-[#007AFF]' : 'text-gray-300'}`} />
                                </button>
                                <AnimatePresence>
                                    {openSection === courseTitle && (
                                        <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden bg-white/20">
                                            {Object.entries(chapters).map(([chName, lessons], chIdx) => (
                                                <div key={chIdx} className="border-t border-white/10">
                                                    {/* ✅ الفتح الذكي للفصل */}
                                                    <button onClick={() => setOpenChapter(openChapter === chName ? "" : chName)} className="w-full p-4 px-6 flex justify-between items-center hover:bg-black/5 text-right font-cairo">
                                                        <span className="text-xs font-bold text-gray-500">{chName}</span>
                                                        <ChevronDownIcon className={`w-4 h-4 transition-transform ${openChapter === chName ? 'rotate-180 text-[#007AFF]' : 'text-gray-300'}`} />
                                                    </button>
                                                    <AnimatePresence>
                                                        {openChapter === chName && (
                                                            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="px-4 pb-4 space-y-2">
                                                                {lessons.map((v) => (
                                                                    <div key={v.id} onClick={() => { 
                                                                        setActiveVideo(v); 
                                                                        setIsVideoEnded(false); 
                                                                        lastTimeRef.current = 0; // تصفير الحماية عند الانتقال لمحاضرة جديدة
                                                                        setOpenSection(v.courseType);
                                                                        setOpenChapter(v.chapter);
                                                                    }} className={`group flex items-center gap-3 p-4 rounded-[22px] cursor-pointer transition-all border ${activeVideo?.id === v.id ? 'bg-[#007AFF] border-transparent text-white shadow-xl scale-[1.03]' : 'bg-white/60 border-white/40 hover:border-[#007AFF]/50 text-gray-700'}`}>
                                                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${activeVideo?.id === v.id ? 'bg-white text-[#007AFF]' : 'bg-white text-gray-400'}`}>
                                                                            <PlayIcon className="w-4 h-4" />
                                                                        </div>
                                                                        <div className="flex-1 overflow-hidden text-right">
                                                                            <h4 className="text-[12px] font-black truncate leading-tight font-cairo">{v.title}</h4>
                                                                        </div>
                                                                        {activeVideo?.id === v.id && <CheckBadgeIcon className="w-5 h-5 text-white animate-pulse" />}
                                                                        {(v.isLocked || v.status !== 'active') && <LockClosedIcon className="w-4 h-4 text-red-500 opacity-70" />}
                                                                    </div>
                                                                ))}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default CourseView;